import React from 'react'
import { Alert, Button, Card, Checkbox, Collapse, Descriptions, Flex, Tag, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import type { AppConnection, ToolSelection } from '../types'
import { ActionChange, actionChange, actionKey, inputSchema, removedActions } from '../tool-configuration'
import {
    approveCapability,
    CapabilityGroup,
    capabilityGroups,
    conversationFields,
    fieldLabel,
    fixedSettingSummary,
    outputSummary,
    displayDate,
    FixedSetting,
    settingValue,
} from '../capability-review'
import { actionPolicy, missingInputs, policyApproved, policyReady } from '../action-policy'
import { ActionPolicyEditor } from './action-policy-editor'
import { AppIcon } from './app-icon'
import { FixedInputs } from './fixed-inputs'

type Props = {
    tools: ToolSelection[]
    connections: AppConnection[]
    baseline?: ToolSelection[]
    disabled?: boolean
    // Hide per-capability approval when the caller approves everything with one action.
    showApproval?: boolean
    invalidKeys?: Record<string, boolean>
    onChange: (tools: ToolSelection[]) => void
    onValidityChange: (key: string, invalid: boolean) => void
}

export function CapabilityReview({
    tools,
    connections,
    baseline,
    disabled,
    showApproval = true,
    invalidKeys = {},
    onChange,
    onValidityChange,
}: Props) {
    const { t } = useTranslation()

    const update = (next: ToolSelection) =>
        onChange(tools.map((tool) => (actionKey(tool) === actionKey(next) ? next : tool)))

    const remove = (removed: ToolSelection[]) => {
        const keys = removed.map(actionKey)

        onChange(tools.filter((tool) => !keys.includes(actionKey(tool))))
    }

    const approve = (group: ToolSelection[], approved: boolean) => {
        const updates = approveCapability(group, approved)

        onChange(tools.map((tool) => updates.find((item) => actionKey(item) === actionKey(tool)) ?? tool))
    }

    return (
        <Flex
            vertical
            gap="small"
        >
            {capabilityGroups(tools).map((group) => (
                <CapabilityGroupCard
                    key={group.key}
                    group={group}
                    app={connections.find((app) => app.id === group.connectionId)}
                    baseline={baseline}
                    disabled={disabled}
                    showApproval={showApproval}
                    invalidKeys={invalidKeys}
                    onApprove={(approved) => approve(group.tools, approved)}
                    onUpdate={update}
                    onRemove={remove}
                    onValidityChange={onValidityChange}
                />
            ))}
            {baseline &&
                removedActions(baseline, tools).map((tool) => (
                    <Flex
                        key={actionKey(tool)}
                        gap="small"
                        align="center"
                    >
                        <Tag color="red">{t('Will be removed')}</Tag>
                        <Typography.Text>{tool.friendlyName ?? tool.name ?? tool.slug}</Typography.Text>
                    </Flex>
                ))}
        </Flex>
    )
}

type CapabilityGroupCardProps = {
    group: CapabilityGroup
    app?: AppConnection
    baseline?: ToolSelection[]
    disabled?: boolean
    showApproval: boolean
    invalidKeys: Record<string, boolean>
    onApprove: (approved: boolean) => void
    onUpdate: (tool: ToolSelection) => void
    onRemove: (tools: ToolSelection[]) => void
    onValidityChange: (key: string, invalid: boolean) => void
}

function CapabilityGroupCard({
    group,
    app,
    baseline,
    disabled,
    showApproval,
    invalidKeys,
    onApprove,
    onUpdate,
    onRemove,
    onValidityChange,
}: CapabilityGroupCardProps) {
    const { t } = useTranslation()
    const connected = app?.status === 'connected'
    const approved = group.tools.every(policyApproved)
    const partlyApproved = !approved && group.tools.some(policyApproved)
    const hasInvalidInput = group.tools.some((tool) => invalidKeys[actionKey(tool)])
    const ready = group.tools.every(policyReady) && connected && !hasInvalidInput
    const needsPersonalRecord = group.tools.some((tool) => actionPolicy(tool).audience === 'personal')
    const title = group.name ?? group.tools[0].friendlyName ?? group.tools[0].name ?? t('App capability')

    // Allowed capabilities show a short summary; the access details stay one click away.
    const compact = showApproval && approved && connected

    const details = (
        <>
            <CapabilitySummary
                tools={group.tools}
                baseline={baseline}
                detailsOnly={compact}
            />
            {showApproval && (
                <Checkbox
                    checked={approved}
                    indeterminate={partlyApproved}
                    disabled={disabled || !ready}
                    onChange={(event) => onApprove(event.target.checked)}
                >
                    {t('Evaa may use this capability with the access shown above.')}
                </Checkbox>
            )}
            {!ready && connected && (
                <Typography.Text type="warning">
                    {needsPersonalRecord
                        ? t(
                              'This needs access to an existing person’s record, which is not supported yet. Ask Evaa for an alternative.'
                          )
                        : t('Complete the missing settings below, or describe them in the chat.')}
                </Typography.Text>
            )}
            <Collapse
                size="small"
                items={[
                    {
                        key: 'advanced',
                        label: t('Advanced settings'),
                        children: (
                            <Flex
                                vertical
                                gap="middle"
                            >
                                <Typography.Text type="secondary">
                                    {t(
                                        'These are the individual app actions behind this capability. Changes to access require a new review.'
                                    )}
                                </Typography.Text>
                                {group.tools.map((tool) => (
                                    <ActionSettings
                                        key={actionKey(tool)}
                                        tool={tool}
                                        baseline={baseline}
                                        disabled={disabled}
                                        onUpdate={onUpdate}
                                        onRemove={() => onRemove([tool])}
                                        onValidityChange={(invalid) => onValidityChange(actionKey(tool), invalid)}
                                    />
                                ))}
                            </Flex>
                        ),
                    },
                ]}
            />
        </>
    )

    return (
        <Card size="small">
            <Flex
                vertical
                gap="middle"
            >
                <Flex
                    align="center"
                    justify="space-between"
                    gap="small"
                    wrap
                >
                    <Flex
                        gap="small"
                        align="center"
                    >
                        <AppIcon
                            name={app?.name ?? ''}
                            toolkit={app?.toolkit ?? ''}
                            size={24}
                        />
                        <Flex vertical>
                            <Typography.Text strong>{title}</Typography.Text>
                            <Typography.Text type="secondary">{app?.name}</Typography.Text>
                        </Flex>
                    </Flex>
                    {showApproval && (
                        <Tag color={approved ? 'green' : 'gold'}>{approved ? t('Allowed') : t('Review needed')}</Tag>
                    )}
                </Flex>
                {!connected && (
                    <Alert
                        type="warning"
                        message={t('Reconnect this app before using this capability.')}
                    />
                )}
                {compact ? (
                    <>
                        <CapabilityActions tools={group.tools} />
                        <Collapse
                            size="small"
                            items={[
                                {
                                    key: 'details',
                                    label: t('Access details'),
                                    children: details,
                                },
                            ]}
                        />
                    </>
                ) : (
                    details
                )}
                <Button
                    size="small"
                    danger
                    className="self-start"
                    disabled={disabled}
                    onClick={() => onRemove(group.tools)}
                >
                    {t('Remove capability')}
                </Button>
            </Flex>
        </Card>
    )
}

type ActionSettingsProps = {
    tool: ToolSelection
    baseline?: ToolSelection[]
    disabled?: boolean
    onUpdate: (tool: ToolSelection) => void
    onRemove: () => void
    onValidityChange: (invalid: boolean) => void
}

// One app action behind a capability, with its fixed inputs and access settings.
function ActionSettings({ tool, baseline, disabled, onUpdate, onRemove, onValidityChange }: ActionSettingsProps) {
    const { t } = useTranslation()

    const changeLabels: Record<ActionChange, string> = {
        added: t('Add action'),
        unchanged: t('Keep action'),
        changed: t('Update existing action'),
    }

    // A changed fixed input needs a new access review.
    const changeFixedArguments = (fixedArgumentsJson: string) =>
        onUpdate({
            ...tool,
            fixedArgumentsJson,
            policyJson: JSON.stringify({ ...actionPolicy(tool), approved: false }),
        })

    return (
        <Collapse
            size="small"
            items={[
                {
                    key: actionKey(tool),
                    label: tool.name ?? tool.slug,
                    children: (
                        <Flex
                            vertical
                            gap="middle"
                        >
                            {baseline && <Tag className="self-start">{changeLabels[actionChange(baseline, tool)]}</Tag>}
                            <Typography.Text type="secondary">{tool.description}</Typography.Text>
                            <FixedInputs
                                schemaJson={tool.inputSchemaJson ?? '{}'}
                                value={tool.fixedArgumentsJson}
                                onValidityChange={onValidityChange}
                                onChange={changeFixedArguments}
                            />
                            <ActionPolicyEditor
                                tool={tool}
                                onChange={onUpdate}
                                showApproval={false}
                            />
                            <Button
                                danger
                                size="small"
                                className="self-start"
                                disabled={disabled}
                                onClick={onRemove}
                            >
                                {t('Remove action')}
                            </Button>
                        </Flex>
                    ),
                },
            ]}
        />
    )
}

function CapabilityActions({ tools, baseline }: { tools: ToolSelection[]; baseline?: ToolSelection[] }) {
    const { t } = useTranslation()

    const changeTags: Record<ActionChange, string> = {
        added: t('New'),
        unchanged: t('Unchanged'),
        changed: t('Changed'),
    }

    return (
        <Flex
            vertical
            gap={4}
        >
            {tools.map((tool) => (
                <Flex
                    key={actionKey(tool)}
                    gap="small"
                    align="center"
                    wrap
                >
                    <Typography.Text>{tool.friendlyName ?? tool.name ?? tool.slug}</Typography.Text>
                    <Tag
                        className="!m-0"
                        color={tool.readOnly ? 'blue' : 'orange'}
                    >
                        {tool.readOnly ? t('Reads information') : t('Makes changes')}
                    </Tag>
                    {baseline && <Tag className="!m-0">{changeTags[actionChange(baseline, tool)]}</Tag>}
                </Flex>
            ))}
        </Flex>
    )
}

type CapabilitySummaryProps = {
    tools: ToolSelection[]
    baseline?: ToolSelection[]
    detailsOnly?: boolean
}

function CapabilitySummary({ tools, baseline, detailsOnly = false }: CapabilitySummaryProps) {
    const { t, i18n } = useTranslation()
    const settings = uniqueSettings(tools, t)
    const outputs = outputSummary(
        tools.flatMap((tool) => actionPolicy(tool).output_paths),
        t
    )
    const missing = [...new Set(tools.flatMap((tool) => missingInputs(tool).map((field) => fieldLabel(field, t))))]
    const audiences = [...new Set(tools.map((tool) => actionPolicy(tool).audience))]

    const inputs = [
        ...new Set(
            tools.flatMap((tool) => {
                const schema = inputSchema(tool)

                return conversationFields(tool).map((field) => fieldLabel(field, t, schema.properties?.[field]?.title))
            })
        ),
    ]

    const display = (value: unknown): string => {
        if (Array.isArray(value)) {
            return value.map(display).join(', ') || t('None')
        }

        if (typeof value === 'boolean') {
            return value ? t('Yes') : t('No')
        }

        if (value === 'none') {
            return t('None')
        }

        if (value === 'primary') {
            return t('Primary')
        }

        return settingValue(value) || t('None')
    }

    // Name the action when two actions have a setting with the same label.
    const settingLabel = (setting: SummarySetting) => {
        const sameLabel = settings.filter((other) => other.label === setting.label)

        if (sameLabel.length > 1) {
            return `${setting.label} (${setting.tool.friendlyName ?? setting.tool.name})`
        }

        return setting.label
    }

    const settingItems = settings.map((setting, index) => ({
        key: `fixed-${index}`,
        label: settingLabel(setting),
        children: (
            <Typography.Text className="break-words">
                <PreviousSettingValue
                    setting={setting}
                    baseline={baseline}
                    display={display}
                />
                {typeof setting.value === 'string'
                    ? displayDate(setting.key, display(setting.value), i18n.language)
                    : display(setting.value)}
            </Typography.Text>
        ),
    }))

    return (
        <Flex
            vertical
            gap="small"
        >
            {!detailsOnly && (
                <CapabilityActions
                    tools={tools}
                    baseline={baseline}
                />
            )}
            <Descriptions
                size="small"
                className="[&_.ant-descriptions-row>td]:!pb-1 [&_.ant-descriptions-row>th]:!pb-1"
                column={1}
                colon={false}
                items={[
                    ...settingItems,
                    {
                        key: 'inputs',
                        label: t('Uses from the conversation'),
                        children: inputs.join(', ') || t('Nothing — all settings are fixed'),
                    },
                    {
                        key: 'outputs',
                        label: t('Evaa can receive'),
                        children: outputs || t('Only whether the action succeeded'),
                    },
                ]}
            />
            {audiences.includes('shared') && (
                <Typography.Text type="secondary">
                    {t('The selected information is available in every participant’s conversation.')}
                </Typography.Text>
            )}
            {audiences.includes('new_record') && (
                <Typography.Text type="secondary">
                    {t('Creates a new record. This does not grant access to existing personal records.')}
                </Typography.Text>
            )}
            {!!missing.length && (
                <Alert
                    type="warning"
                    message={t('Still needed: {{settings}}', { settings: missing.join(', ') })}
                />
            )}
        </Flex>
    )
}

type SummarySetting = FixedSetting & {
    tool: ToolSelection
    label: string
}

// The fixed settings of all actions, without repeating a label with the same value.
function uniqueSettings(tools: ToolSelection[], translate: (key: string) => string): SummarySetting[] {
    const settings = tools.flatMap((tool) =>
        fixedSettingSummary(tool).map((setting) => ({
            ...setting,
            tool,
            label: fieldLabel(setting.key, translate, setting.title),
        }))
    )

    return settings.filter((setting, index) => {
        const firstIndex = settings.findIndex(
            (other) => other.label === setting.label && JSON.stringify(other.value) === JSON.stringify(setting.value)
        )

        return firstIndex === index
    })
}

type PreviousSettingValueProps = {
    setting: SummarySetting
    baseline?: ToolSelection[]
    display: (value: unknown) => string
}

// The struck-through old value when the proposal changes a fixed setting.
function PreviousSettingValue({ setting, baseline, display }: PreviousSettingValueProps) {
    const previous = baseline?.find((tool) => actionKey(tool) === actionKey(setting.tool))

    if (!previous) {
        return null
    }

    const oldFixed = JSON.parse(previous.fixedArgumentsJson) as Record<string, unknown>
    const hadSetting = Object.prototype.hasOwnProperty.call(oldFixed, setting.key)

    if (!hadSetting || JSON.stringify(oldFixed[setting.key]) === JSON.stringify(setting.value)) {
        return null
    }

    return (
        <>
            <Typography.Text
                delete
                type="secondary"
            >
                {display(oldFixed[setting.key])}
            </Typography.Text>
            {' → '}
        </>
    )
}
